import { OrganisationResponse } from 'api';
import { useAuth } from 'contexts/AuthContext';
import { updateCurrentOrg } from 'pages/AddUser/AddUserForm/helper';
import { useInviteContext } from 'pages/AddUser/InviteProvider';
import { useEffect, useMemo, useState } from 'react';
import { ExistingUserSection } from './ExistingUserSection';
import { InviteUserSection } from './InviteUserSection';
import { ExistingUserSectionProps, InviteSectionProps } from './proptypes';

const filterPreExistingOrganisations =
  (organisations: OrganisationResponse[]) =>
  ({ id }: OrganisationResponse) =>
    !organisations.some(({ id: pOrgId }) => id === pOrgId);

type OrgsAndDetailsSectionProps = {
  formEmailAddress?: string;
  selectedOrgs: OrganisationResponse[];
  setSelectedOrgs: (org: any) => void;
  onRemoveOrg: (id: string) => void;
  control: any;
  setValue: (...args: any) => void;
  userAlreadyHasAccess?: boolean;
  currentOrgId: string;
  emailLoading: boolean;
};

export const OrgsAndDetailsSection = ({
  formEmailAddress,
  selectedOrgs,
  setSelectedOrgs,
  onRemoveOrg,
  control,
  setValue,
  currentOrgId,
  emailLoading,
  userAlreadyHasAccess,
}: OrgsAndDetailsSectionProps) => {
  const { user } = useAuth();
  const [ignoreState, setIgnoreState] = useState(false);
  const {
    rawOrganisations,
    practitionerOrganisations,
    rawPractitionerOrganisations,
    isPending,
    canInviteCurrentPractitioner,
    formState,
    setFormState,
  } = useInviteContext();

  const { selectedGroup, currentOrg, orgType, existingPractitioner } =
    formState;
  const canInvitePractitioner =
    !existingPractitioner &&
    user?.permissions.Organisation_InvitePractitioner === 'Global';
  const canAddPractitioner =
    existingPractitioner &&
    user?.permissions.Organisation_Add_User === 'Global';
  const hideComponent =
    (isPending && existingPractitioner) ||
    !formEmailAddress ||
    emailLoading ||
    !canInviteCurrentPractitioner ||
    userAlreadyHasAccess ||
    existingPractitioner?.errors;

  const organisations = useMemo(
    () =>
      rawOrganisations.filter(
        filterPreExistingOrganisations(rawPractitionerOrganisations),
      ),
    [rawOrganisations, rawPractitionerOrganisations],
  );
  const groupOptions =
    selectedGroup?.organisations.filter(
      filterPreExistingOrganisations(rawPractitionerOrganisations),
    ) ?? [];

  const commonProps: InviteSectionProps & ExistingUserSectionProps = {
    user,
    selectedOrgs,
    organisations,
    onRemoveOrg,
    groupOptions,
    formEmailAddress,
    setValue,
    control,
    setSelectedOrgs: (org) => {
      setIgnoreState(orgType === 'ONE_ORG');
      setSelectedOrgs(org);
    },
  };

  useEffect(() => {
    if (
      formEmailAddress &&
      !emailLoading &&
      selectedOrgs?.length < 1 &&
      orgType === 'ONE_ORG' &&
      !practitionerOrganisations?.find(({ id }) => currentOrgId === id)
    ) {
      if (!ignoreState) {
        updateCurrentOrg(
          organisations,
          (organisation) => {
            setFormState({
              currentOrg: organisation,
              selectedGroup: null,
            });
            setSelectedOrgs(organisation);
          },
          currentOrgId,
          practitionerOrganisations,
        );
      }
    }

    if (
      practitionerOrganisations?.find(({ id }) => currentOrgId === id) &&
      selectedOrgs?.find(({ id }) => currentOrgId === id)
    ) {
      setFormState({ currentOrg: undefined });
      setSelectedOrgs(null);
    }
  }, [
    currentOrg,
    formEmailAddress,
    currentOrgId,
    organisations,
    practitionerOrganisations,
    existingPractitioner,
    setSelectedOrgs,
    selectedOrgs,
    emailLoading,
    setFormState,
    orgType,
    ignoreState,
  ]);

  if (hideComponent) {
    return null;
  }

  if (existingPractitioner && canAddPractitioner) {
    return <ExistingUserSection {...commonProps} />;
  }

  if (!existingPractitioner && canInvitePractitioner) {
    return <InviteUserSection {...commonProps} />;
  }

  return null;
};
